import {
  shortInstitutionName,
  type PortalClientFile,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

/* What the Messages list and the attach list say about each row. Plain
   logic, so the names can be tested without a browser. */

type ThreadSummary = {
  subject: string
  requestItemId: string | null
  resolvedAt: string | null
  lastAuthorSide: "firm" | "client" | null
}

/** A word joiner after "…", so "…4471" never breaks onto two lines. */
export const keepAccountTailTogether = (text: string): string =>
  text.replace(/…(?=\d)/g, "…⁠")

/**
 * A thread's name in the list (R3-U10). A thread about an item that is
 * still on the client's list takes the item's name as it reads today, so
 * the list and the task list agree. Any other keeps its subject, less the
 * "(2)" a duplicate question name was given and the bank's legal name.
 */
export const threadRowName = (
  thread: Pick<ThreadSummary, "subject" | "requestItemId">,
  requests: readonly Pick<PortalRequestResponse, "items">[]
): string => {
  const item = thread.requestItemId
    ? requests
        .flatMap((request) => request.items)
        .find((candidate) => candidate.itemId === thread.requestItemId)
    : undefined
  const name =
    item?.name ??
    thread.subject
      .replace(/\s+\(\d+\)$/, "")
      .replace(/ on (.+?) …(\d{4})/, (_whole, bank: string, last4: string) =>
        ` on ${shortInstitutionName(bank)} …${last4}`
      )
  return keepAccountTailTogether(name)
}

/** An open thread where the firm spoke last: it is what the Messages tab
 * counts as new, so the row is marked new too. */
export const threadAwaitsClient = (
  thread: Pick<ThreadSummary, "resolvedAt" | "lastAuthorSide">
): boolean => !thread.resolvedAt && thread.lastAuthorSide === "firm"

/**
 * The attach list (R3-U11): each file once, newest first. Files that share
 * a name carry when they came in, so the rows can be told apart.
 */
export const attachableFiles = (
  files: readonly PortalClientFile[],
  when: (iso: string) => string
): { fileId: string; label: string }[] => {
  const available = files
    .filter((file) => file.blobPurgedAt === null)
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
  const counts = new Map<string, number>()
  for (const file of available)
    counts.set(file.fileName, (counts.get(file.fileName) ?? 0) + 1)
  return available.map((file) => ({
    fileId: file.fileId,
    label:
      (counts.get(file.fileName) ?? 0) > 1
        ? `${file.fileName}, sent ${when(file.receivedAt)}`
        : file.fileName,
  }))
}
